<script setup>
import { computed, ref, watch } from 'vue'
import { getRecord } from '@/lib/records.js'
import { reportError } from '@/composables/useSave.js'
import { useUi } from '@/stores/ui.js'
import RecordForm from '@/components/form/RecordForm.vue'
import RecordSections from '@/components/record/RecordSections.vue'
import SuggestionBar from '@/components/record/SuggestionBar.vue'
import Timeline from '@/components/record/Timeline.vue'
import UiSpinner from '@/components/ui/UiSpinner.vue'

/**
 * A record in a panel from the right (opened from a reference): its form — editable as on its
 * page — what it shows under the form, and its time line. Saved, the page underneath is told.
 */
const props = defineProps({ entity: { type: String, required: true }, uuid: { type: String, required: true } })
const emit = defineEmits(['loaded', 'saved'])
const ui = useUi()
const record = ref(null)
const form = ref(null)
const formKey = ref(0)
const timeline = ref(null)
const can = (ability) => record.value?.abilities?.[ability] === true
const editable = computed(() => can('update'))

let loads = 0
async function load() {
  const ticket = ++loads
  try {
    const data = (await getRecord(props.entity, props.uuid)).data
    // Only the latest reading counts.
    if (ticket !== loads) return
    record.value = data
    // A fresh reading renews the form (its version with it), unless the person is typing in it.
    if (!form.value?.dirty) formKey.value++
    emit('loaded', record.value)
  } catch (e) {
    reportError(e)
  }
}
watch(() => [props.entity, props.uuid], load, { immediate: true })
const refresh = () => {
  load()
  timeline.value?.reload()
}
// What its sections changed (an agreement's stages, a contract's figures): the page underneath is told too.
const sectionsChanged = () => {
  refresh()
  ui.recordSaved(props.entity, props.uuid)
}
function saved(r, result) {
  if (result?.status === 'unchanged') return
  ui.recordSaved(props.entity, props.uuid)
  formKey.value++
  refresh()
  emit('saved', result)
}
defineExpose({
  // Changed against the record as saved (as a page's unsaved bar counts), not against the form's first state.
  changed: computed(() => form.value?.dirty ?? false),
  submit: () => form.value?.submit(),
})
</script>

<template>
  <div v-if="!record" class="py-16 text-center"><UiSpinner size="lg" /></div>
  <div v-else class="space-y-4" :data-testid="`record-panel-${entity}`">
    <SuggestionBar :subject="{ entity, uuid: record.uuid }" :source="{ entity, uuid: record.uuid }" @applied="load" />
    <div class="card p-4">
      <RecordForm ref="form" :key="formKey" hide-actions :entity="entity" :record="record" :readonly="!editable" @saved="saved" @cancel="formKey++" />
    </div>
    <RecordSections :entity="entity" :record="record" :can="can" @reload="load" @refresh="sectionsChanged" />
    <Timeline ref="timeline" :entity="entity" :record="record" />
  </div>
</template>
